import type { ReactNode } from 'react';
import {
  DrawStatus,
  NotificationStatus,
  PhotoScanStatus,
  ReceiptStatus,
  TicketStatus,
} from '@qaytim/shared';

import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type Tone = 'default' | 'secondary' | 'destructive' | 'success' | 'warning' | 'outline';

/**
 * Holat qiymatlari uchun rang.
 *
 * MATN tarjima qilinmaydi — ekranda `receipts.status` ustunidagi qiymatning O'ZI turadi, chunki
 * operator qo'llab-quvvatlash so'rovida va loglarda aynan shu so'zni izlaydi. Rang esa faqat
 * ustama qatlam: "qaysi qatorga birinchi qarash kerak" degan savolga javob beradi.
 */
const TONES: Record<string, Tone> = {
  // Cheklar
  [ReceiptStatus.PENDING_DETAILS]: 'secondary',
  [ReceiptStatus.DETAILS_FETCHING]: 'warning',
  [ReceiptStatus.DETAILS_READY]: 'success',
  [ReceiptStatus.DETAILS_FAILED]: 'destructive',
  // Rasm skanlari
  [PhotoScanStatus.AWAITING_UPLOAD]: 'secondary',
  [PhotoScanStatus.IN_REVIEW]: 'warning',
  [PhotoScanStatus.CONFIRMED]: 'success',
  [PhotoScanStatus.REJECTED]: 'destructive',
  [PhotoScanStatus.VOID]: 'outline',
  // Biletlar
  [TicketStatus.FRAUD_REVIEW]: 'warning',
  [TicketStatus.REVOKED]: 'destructive',
  [TicketStatus.WON]: 'success',
  // Xabarlar
  [NotificationStatus.FAILED]: 'destructive',
  [NotificationStatus.SENT]: 'success',
  // Tortishuvlar
  [DrawStatus.SCHEDULED]: 'secondary',
  [DrawStatus.COMPLETED]: 'success',
  [DrawStatus.POSTPONED]: 'warning',
};

export interface StatusBadgeProps {
  /** Ko'rinadigan matn. Server holatlari uchun — qiymatning o'zi, tarjimasiz. */
  children: ReactNode;
  /**
   * Rangni tanlash uchun xom holat kaliti.
   *
   * Berilishi — "matn tarjima qilingan" degani ham: bunday yorliq odatiy shriftda chiziladi,
   * xom qiymat esa mono shriftda, chunki u bazadagi so'zning aynan o'zi.
   */
  status?: string;
  className?: string;
}

export function StatusBadge({ children, status, className }: StatusBadgeProps) {
  const raw = status === undefined;
  const key = status ?? (typeof children === 'string' ? children : '');
  const tone = TONES[key] ?? 'outline';

  return (
    <Badge variant={tone} className={cn(raw && 'font-mono text-[11px]', className)}>
      {children}
    </Badge>
  );
}
